import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import { Toaster } from "sonner";
import { api } from "@/api";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Portal from "@/portal/Portal";
import InvoicePrint from "@/portal/InvoicePrint";
import QuotationPrint from "@/portal/QuotationPrint";
import PaymentPrint from "@/portal/PaymentPrint";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: returning from OAuth callback — let AuthCallback exchange session_id first
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, refresh: checkAuth, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

function AuthCallback() {
  const { setUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sid = new URLSearchParams(location.hash.slice(1)).get("session_id");
    api.post("/auth/session", { session_id: sid })
      .then((r) => {
        setUser(r.data);
        navigate("/app", { replace: true });
      })
      .catch(() => navigate("/login", { replace: true }));
  }, [location, navigate, setUser]);

  return (
    <div className="min-h-screen bg-[#F3F6F5] flex items-center justify-center" data-testid="auth-callback-loading">
      <div className="font-mono2 text-sm text-emerald-600 animate-pulse">Signing you in…</div>
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen bg-[#F3F6F5] flex items-center justify-center" data-testid="auth-loading">
        <div className="font-mono2 text-sm text-emerald-600 animate-pulse">Loading…</div>
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/invoice/:id/print" element={<Protected><InvoicePrint /></Protected>} />
      <Route path="/quotation/:id/print" element={<Protected><QuotationPrint /></Protected>} />
      <Route path="/payment/:id/print" element={<Protected><PaymentPrint /></Protected>} />
      <Route path="/app/*" element={<Protected><Portal /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster theme="light" position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
